import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { iconManager } from '../utils/iconManager';

// Domain-specific icon component with JPG/PNG support
const DomainIcon = ({ 
  className = 'w-6 h-6', 
  alt, 
  fallbackIcon = '/assets/icons/default',
  ...props 
}) => {
  const { brand } = useTheme();
  const [currentSrc, setCurrentSrc] = useState(null);
  const [hasError, setHasError] = useState(false);

  // Reset state when brand changes (domain switch)
  useEffect(() => {
    setCurrentSrc(null);
    setHasError(false);
  }, [brand.id, brand.icon]);

  // Get icon path
  const iconPath = brand.icon || iconManager.getDomainIcon(brand.id);
  const fallbackPath = iconManager.findIconWithFormat(fallbackIcon);
  

  // Handle image error with format fallback
  const handleError = (event) => {
    if (hasError) return; // Prevent infinite retry

    // Extract just the pathname from the src (removes localhost or any domain)
    // This prevents localhost URLs from being baked into the pre-rendered HTML
    let currentPath;
    try {
      const url = new URL(event.target.src);
      currentPath = url.pathname; // Just the path, e.g., "/assets/icons/fleet-dashcam.png"
    } catch {
      currentPath = event.target.src; // Fallback if already relative
    }
    
    const basePath = currentPath.replace(/\.[^/.]+$/, '');
    
    // Try different extensions
    const extensions = ['png', 'jpg', 'jpeg'];
    const currentExt = currentPath.split('.').pop().toLowerCase();
    const remainingExts = extensions.filter(ext => ext !== currentExt);
    
    // Try remaining extensions (always use relative paths)
    for (const ext of remainingExts) {
      const newPath = `${basePath}.${ext}`;
      if (newPath !== currentPath) {
        setCurrentSrc(newPath);
        return;
      }
    }
    
    // If all formats fail, use fallback (relative path)
    if (fallbackPath && currentPath !== fallbackPath) {
      setCurrentSrc(fallbackPath);
    } else {
      setHasError(true);
    }
  };

  const handleLoad = () => {
    setHasError(false);
  };

  return (
    <img
      src={currentSrc || iconPath}
      alt={alt || `${brand.name} icon`}
      className={className}
      onError={handleError}
      onLoad={handleLoad}
      {...props}
    />
  );
};

export default DomainIcon;
